import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import "./App.css";
import { CartProvider } from "./Component/CartContext";
import Header from "./Component/Header";
import Footer from "./Component/Footer";

const Main = lazy(() => import("./Component/Main"));
const Shop = lazy(() => import("./Component/Shop"));
const ProductDetail = lazy(() => import("./Component/ProductDetail"));
const CartPage = lazy(() => import("./Component/CartPage"));
const CheckOut = lazy(() => import("./Component/CheckOut"));
const PaySuccess = lazy(() => import("./Component/SuccessPay"));
const About = lazy(() => import("./Component/About"));
const Contact = lazy(() => import("./Component/Contact"));
const Privacy = lazy(() => import("./Component/Privacy"));
const Terms = lazy(() => import("./Component/Terms"));
const Returns = lazy(() => import("./Component/Returns"));
const Shipping = lazy(() => import("./Component/Delivery"));
const Faqs = lazy(() => import("./Component/Faqs"));
const Cookies = lazy(() => import("./Component/Cookies"));
const PolicyCenter = lazy(() => import("./Component/PolicyCenter"));
const EcommercePolicy = lazy(() => import("./Component/EcommercePolicy"));
const NotFound = lazy(() => import("./Component/NotFound"));

function RouteEffects() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    if (typeof window.scrollTo === "function") {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [pathname]);

  return null;
}

function RouteLoading() {
  return (
    <main className="route-loading" aria-live="polite" aria-busy="true">
      <span className="route-loading__mark">d</span>
      <p>Opening the next page…</p>
    </main>
  );
}

class StorefrontErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="page-error">
          <p className="eyebrow">Something went sideways</p>
          <h1>This page could not be displayed.</h1>
          <p>Your cart is safe. Refresh the page or head back to the collection.</p>
          <a className="button button--primary" href="/shop">
            Return to shop
          </a>
        </main>
      );
    }

    return this.props.children;
  }
}

function StorefrontRoutes() {
  return (
    <>
      <RouteEffects />
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <Header />
      <Suspense fallback={<RouteLoading />}>
        <Routes>
          <Route path="/" element={<Main />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/collections/:collection" element={<Shop />} />
          <Route path="/shopDetails/:id" element={<ProductDetail />} />
          <Route path="/products/:slug" element={<ProductDetail />} />
          <Route path="/cartpage" element={<CartPage />} />
          <Route path="/cart" element={<Navigate replace to="/cartpage" />} />
          <Route path="/checkout" element={<CheckOut />} />
          <Route path="/paysuccess" element={<PaySuccess />} />
          <Route path="/payment" element={<PaySuccess />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/shipping" element={<Shipping />} />
          <Route path="/faqs" element={<Faqs />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/policies" element={<PolicyCenter />} />
          <Route path="/policies/:policySlug" element={<EcommercePolicy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Footer />
    </>
  );
}

function App() {
  return (
    <StorefrontErrorBoundary>
      <CartProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <StorefrontRoutes />
        </BrowserRouter>
      </CartProvider>
    </StorefrontErrorBoundary>
  );
}

export default App;
